import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Combobox, Field, Input } from '@repo/design-system';
import { space } from '@repo/design-tokens';
import { countryCodeFor, countryOptions } from '@repo/front-end-utils';

import { ADDRESS_FIELDS, type TwinDetailsForm } from './twinDetailsForm';

/**
 * The country and the five typed address lines — ONE component, rendered by the create route's
 * *Where is it?* card and by the summary's Address card in edit (`twin-details-page-summary`, R4.2:
 * lifted out of `TwinDetailsWhere`, never copied).
 *
 * The save points are the caller's and the same everywhere: the country on choose, a line when it
 * loses focus with a changed value (`twin-details-page-address`, R2–R3). Escape in a line puts its
 * saved value back (summary R2.4), as the coordinate pair already does.
 */

export type AddressLine = (typeof ADDRESS_FIELDS)[number];

/** The five lines, in R3.1's order, with the `autoComplete` each always had. */
export const ADDRESS_LINES: Array<{ field: AddressLine; label: string; autoComplete: string }> = [
  { field: 'address_1', label: 'Address line 1', autoComplete: 'address-line1' },
  { field: 'address_2', label: 'Address line 2', autoComplete: 'address-line2' },
  { field: 'city', label: 'Town / city', autoComplete: 'address-level2' },
  { field: 'state', label: 'County / state', autoComplete: 'address-level1' },
  { field: 'postcode', label: 'Postcode', autoComplete: 'postal-code' },
];

interface Props {
  className?: string;
  /** The twin's saved country text — a code, a name, an alias, free text or blank. */
  storedCountry: string;
  form: TwinDetailsForm;
  onCountry: (code: string | null) => void;
  onLineChange: (field: AddressLine, value: string) => void;
  onLineBlur: (field: AddressLine) => void;
  /** Escape in a line: put its saved value back. */
  onLineRestore: (field: AddressLine) => void;
}

const AddressFields: React.FC<Props> = ({
  className,
  storedCountry,
  form,
  onCountry,
  onLineChange,
  onLineBlur,
  onLineRestore,
}) => {
  const countries = useMemo(
    () => countryOptions().map(c => ({ value: c.code, label: c.name })),
    [],
  );
  const stored = storedCountry.trim();
  const code = countryCodeFor(stored);
  // R2.5: text that is not a listed country stays in the field as typed, with nothing chosen. The
  // Combobox draws a value unknown to its options as raw text, and its note says why.
  const notInList = stored !== '' && code === null;

  return (
    <div className={className}>
      <Field
        label="Country"
        helper={countries.length === 0 ? 'This browser can’t list countries.' : undefined}
      >
        {wiring => (
          <Combobox
            {...wiring}
            options={countries}
            value={code ?? (notInList ? stored : undefined)}
            placeholder="Choose the country"
            // Every country: the list is the engine's regions with a zone (about 250), and a
            // country past a cap would be missing, not scrolled to.
            maxResults={Infinity}
            notInListNote={
              notInList ? `“${stored}” isn’t in the list. Choose the country.` : undefined
            }
            refusedText={typed => `“${typed}” isn’t in the list. Choose the country.`}
            emptyText="No country by that name"
            clearLabel="Clear the country"
            onChange={value => onCountry(value)}
          />
        )}
      </Field>
      <div className="lines">
        {ADDRESS_LINES.map(({ field, label, autoComplete }) => (
          <Field key={field} label={label}>
            {wiring => (
              <Input
                {...wiring}
                autoComplete={autoComplete}
                value={form[field]}
                onChange={event => onLineChange(field, event.target.value)}
                onBlur={() => onLineBlur(field)}
                onKeyDown={event => {
                  if (event.key === 'Escape') {
                    // Marked, so a card or popover around the field leaves this Escape alone.
                    event.preventDefault();
                    onLineRestore(field);
                  }
                }}
              />
            )}
          </Field>
        ))}
      </div>
    </div>
  );
};

export default styled(AddressFields)`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};

  .lines {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: ${space[4]};
    align-items: start;
  }
`;
